import { useMemo, useState } from 'react';

import { mapQuery } from 'utils/query';
import { useAppSelector } from 'store';
import { Flex } from 'components/flex';
import { formatDate } from 'utils/date';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { Payout, useListQuery } from 'store/api/payout';
import { formatPrice, joinStrings } from 'utils/helpers';
import { PayoutType, PayoutStatus, ListCardStyle } from 'types';
import { PayoutStatusBadge } from 'components/payout-status-badge';
import { RemovePayoutDialog } from 'components/remove-payout-dialog';
import { ApprovalPayoutDialog } from 'components/approval-payout-dialog';
import { isDesktop, useFilter, useShowMore, useSearchQuery } from 'hooks';
import {
  approveReimbursementRoles,
  canDeleteReimbursementStatuses,
} from 'config';
import {
  isAuthCompanyUser,
  isEventManagerRole,
  selectAuthUserRole,
} from 'store/slices/auth';

import * as CommonStyles from '../styles';

import * as Styles from './styles';
import { ReimbursementProps } from './types';
import { CreateReimbursement } from './create';
import { ReimbursementSummary } from './summary';
import { ReimbursementDrawerDetails } from './details';
import { isPrepaid, getReimbursementTotals } from './totals';

const titles = [
  'Date',
  'Employee',
  'Description',
  'Amount',
  'Category',
  'Status',
];

const getTemplateAndTitles = (canApproveReimbursement: boolean) => {
  let template = 'repeat(6, 1fr)';
  const currentTitles = [...titles];

  if (canApproveReimbursement) {
    template += ' 184px';
    currentTitles.push('Approve');
  }

  return { template, currentTitles };
};

export const Reimbursement = ({ event }: ReimbursementProps) => {
  const desktop = isDesktop();
  const isManager = useAppSelector(isEventManagerRole);
  const authUserRole = useAppSelector(selectAuthUserRole);
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);
  const canApproveReimbursement =
    isCompanyOwner ||
    !!(authUserRole && approveReimbursementRoles.includes(authUserRole));

  const [selectedReimbursementId, setSelectedReimbursementId] =
    useState<number>();

  const { debounceQuery, ...search } = useSearchQuery();
  const { data, ...props } = useListQuery({
    eventId: String(event.id),
    // Every reimbursement the P&L counts (owner 2026-10-17), incl. the cash
    // given in advance; bonuses stay out of this tab.
    types: JSON.stringify([
      PayoutType.Expense,
      PayoutType.Reimbursements,
      PayoutType.PrepaidExpense,
      PayoutType.PrepaidReimbursements,
    ]),
  });

  const filteredValues = useFilter<Payout>({
    data: data || [],
    query: debounceQuery,
    paths: [
      'date',
      'notes',
      'amount',
      'event.name',
      'category.name',
      'employee.user.name',
    ],
  });

  const canCreateReimbursement = authUserRole && (isManager || isCompanyOwner);

  const { visibleData, ...showMoreProps } = useShowMore(filteredValues);
  const totals = useMemo(
    () => getReimbursementTotals(filteredValues),
    [filteredValues]
  );
  const totalLabel = `Total · ${totals.counted} record${
    totals.counted === 1 ? '' : 's'
  }`;
  const { template, currentTitles } = getTemplateAndTitles(
    canApproveReimbursement
  );

  return (
    <>
      <CommonStyles.StyledList
        {...mapQuery(props)}
        {...search}
        {...showMoreProps}
        count={visibleData?.length}
        header={<ListHeader template={template} titles={currentTitles} />}
        topHeader={
          filteredValues.length ? (
            <ReimbursementSummary totals={totals} />
          ) : undefined
        }
        searchBarRightSlot={
          canCreateReimbursement ? (
            <CreateReimbursement event={event} />
          ) : undefined
        }
        footer={
          filteredValues.length ? (
            // Inset like the list's rows, so the total uses the same columns.
            <Styles.Footer>
              <ListCard
                isOdd
                selected
                padding="16px"
                withPlaceholders={false}
                mobileTemplate="1fr auto"
                tabletTemplate="1fr auto"
                odd={ListCardStyle.Default}
                // On a desktop the total sits in the Amount column; narrower
                // screens show the rows as cards and the total as one card.
                template={desktop ? template : '1fr auto'}
                headers={desktop ? currentTitles.map(() => '') : undefined}
                values={
                  desktop
                    ? [
                        <Styles.TotalText key="label">
                          {totalLabel}
                        </Styles.TotalText>,
                        '',
                        '',
                        <Styles.TotalText
                          key="amount"
                          data-testid="reimbursement-total"
                        >
                          {formatPrice(totals.total)}
                        </Styles.TotalText>,
                        '',
                        '',
                        ...(canApproveReimbursement ? [''] : []),
                      ]
                    : [
                        <Styles.TotalText key="label">
                          {totalLabel}
                        </Styles.TotalText>,
                        <Styles.TotalText
                          key="amount"
                          data-testid="reimbursement-total"
                        >
                          {formatPrice(totals.total)}
                        </Styles.TotalText>,
                      ]
                }
              />
            </Styles.Footer>
          ) : undefined
        }
      >
        {visibleData?.map((value) => {
          const values = [
            formatDate(value?.date),
            value?.employee?.user?.name,
            value?.notes,
            formatPrice(value?.amount),
            isPrepaid(value)
              ? joinStrings([value?.category?.name, 'Prepaid'], ' · ')
              : value?.category?.name,
            <PayoutStatusBadge type={value?.type} status={value?.status} />,
          ];

          const isPending = value?.status === PayoutStatus.Pending;

          if (
            canApproveReimbursement &&
            canDeleteReimbursementStatuses.includes(value?.status)
          ) {
            values.push(
              <Flex gap={8}>
                {isPending && <ApprovalPayoutDialog data={value} />}
                <RemovePayoutDialog data={value} withoutNavigate />
              </Flex>
            );
          }

          return (
            <ListCard
              padding="16px"
              key={value.id}
              values={values}
              template={template}
              headers={currentTitles}
              onClick={() => setSelectedReimbursementId(value.id)}
            />
          );
        })}
      </CommonStyles.StyledList>

      <ReimbursementDrawerDetails
        selectedReimbursementId={selectedReimbursementId}
        onClose={() => setSelectedReimbursementId(undefined)}
      />
    </>
  );
};
